import React, { useEffect, useMemo } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  ClientInvestment,
  ClientLocation,
  InvestmentPaymentMode,
  InvestmentPaymentType,
} from "../types";

interface FormValues {
  name: string;
  description?: string;
  totalAmount: number;
  paymentType: InvestmentPaymentType;
  installmentsCount?: number;
  startDate: dayjs.Dayjs;
  paymentMode: InvestmentPaymentMode;
  locationId?: string | null;
  status?: "ACTIVE" | "COMPLETED";
}

interface Props {
  open: boolean;
  loading: boolean;
  onSubmit: (values: FormValues) => Promise<void>;
  onClose: () => void;
  investment?: ClientInvestment | null;
  locations?: ClientLocation[];
}

const InvestmentFormModal: React.FC<Props> = ({
  open,
  loading,
  onSubmit,
  onClose,
  investment,
  locations = [],
}) => {
  const [form] = Form.useForm<FormValues>();
  const { t } = useTranslation();

  const paymentType = Form.useWatch("paymentType", form);
  const totalAmount = Form.useWatch("totalAmount", form);
  const installmentsCount = Form.useWatch("installmentsCount", form);

  const monthlyAmount = useMemo(() => {
    if (paymentType === "INSTALLMENTS") {
      const count = Number(installmentsCount || 1);
      if (!count) return 0;
      return Number((Number(totalAmount || 0) / count).toFixed(2));
    }
    return Number(totalAmount || 0);
  }, [paymentType, totalAmount, installmentsCount]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
    } else if (investment) {
      form.setFieldsValue({
        name: investment.name,
        description: investment.description,
        totalAmount: investment.totalAmount,
        paymentType: investment.paymentType,
        installmentsCount: investment.installmentsCount,
        startDate: investment.startDate ? dayjs(investment.startDate) : dayjs(),
        paymentMode: investment.paymentMode,
        locationId:
          typeof investment.locationId === "object"
            ? investment.locationId?._id
            : investment.locationId,
        status: investment.status,
      });
    } else {
      form.setFieldsValue({
        startDate: dayjs(),
        paymentType: "ONE_TIME" as InvestmentPaymentType,
        paymentMode: "AUTO" as InvestmentPaymentMode,
      });
    }
  }, [open, form, investment]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values);
      form.resetFields();
    } catch {
      // validation or submit handled upstream
    }
  };

  return (
    <Modal
      open={open}
      title={investment ? t("Edit investment") : t("New client investment")}
      className="standardHeader standardFooter ci-form-modal"
      onCancel={onClose}
      onOk={handleOk}
      okText={investment ? t("Update") : t("Save")}
      cancelText={t("Cancel")}
      confirmLoading={loading}
      destroyOnClose
      width={520}
    >
      <Form layout="vertical" form={form}>
        <Form.Item
          name="name"
          label={t("Investment name")}
          rules={[{ required: true, message: t("Please enter investment name") }]}
        >
          <Input placeholder={t("e.g. Platform renovation — materials")} />
        </Form.Item>

        <Form.Item name="description" label={t("Description")}>
          <Input.TextArea rows={2} placeholder={t("Optional description")} />
        </Form.Item>

        <div className="ci-two-col">
          <Form.Item name="locationId" label={t("Location")}>
            <Select
              allowClear
              showSearch
              placeholder={t("Select location")}
              options={(locations || []).map((loc) => ({
                label: loc.name,
                value: loc._id,
              }))}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="startDate"
            label={t("Start date")}
            rules={[{ required: true, message: t("Please select start date") }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </div>

        <Form.Item
          name="totalAmount"
          label={t("Total investment amount")}
          rules={[{ required: true, message: t("Please enter total amount") }]}
        >
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            placeholder="0"
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          />
        </Form.Item>

        <div className="ci-two-col">
          <Form.Item name="paymentType" label={t("Payment type")}>
            <Select
              options={[
                { label: t("One-time"), value: "ONE_TIME" },
                { label: t("Installments"), value: "INSTALLMENTS" },
              ]}
            />
          </Form.Item>

          <Form.Item name="paymentMode" label={t("Payment mode")}>
            <Select
              options={[
                { label: t("Auto deduct monthly"), value: "AUTO" },
                { label: t("Manual payment tracking"), value: "MANUAL" },
              ]}
            />
          </Form.Item>
        </div>

        {paymentType === "INSTALLMENTS" && (
          <>
            <Form.Item
              name="installmentsCount"
              label={t("Number of installments (months)")}
              rules={[{ required: true, message: t("Please provide installment count") }]}
            >
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>

            <div className="ci-monthly-preview">
              <span>{t("Monthly amount")}</span>
              <strong>
                {monthlyAmount.toLocaleString()} RSD
              </strong>
            </div>
          </>
        )}

        {investment && (
          <Form.Item name="status" label={t("Status")} style={{ marginTop: 16 }}>
            <Select
              options={[
                { label: t("Active"), value: "ACTIVE" },
                { label: t("Completed"), value: "COMPLETED" },
              ]}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default InvestmentFormModal;
